import { useState, useMemo } from "react";
import { venues } from "../data";
import { StripeCardForm } from "./StripeCardForm";
import { API, formatTime, parseLocalDate } from "./calendarUtils";

export function WorkshopBookingModal({ workshop, onClose }) {
  const [selected, setSelected]   = useState(new Set());
  const [name, setName]           = useState("");
  const [email, setEmail]         = useState("");
  const [booked, setBooked]       = useState(false);
  const [bookError, setBookError] = useState("");

  const totalPrice = useMemo(() => {
    if (selected.has("full")) return workshop.pricing.fullWorkshop;
    let total = 0;
    workshop.days.forEach((day, di) => {
      if (selected.has(`day__${di}`)) {
        total += workshop.pricing.fullDay;
      } else {
        day.sessions.forEach(sess => {
          if (selected.has(`${di}__${sess.id}`)) total += sess.price;
        });
      }
    });
    return total;
  }, [selected, workshop]);

  const toggle = (key) => setSelected(prev => {
    const next = new Set(prev);
    if (next.has(key)) { next.delete(key); } else { next.delete("full"); next.add(key); }
    return next;
  });

  const toggleFull = () => setSelected(prev => prev.has("full") ? new Set() : new Set(["full"]));

  const toggleDay = (di) => setSelected(prev => {
    const next = new Set(prev);
    next.delete("full");
    if (next.has(`day__${di}`)) {
      next.delete(`day__${di}`);
    } else {
      workshop.days[di].sessions.forEach(s => next.delete(`${di}__${s.id}`));
      next.add(`day__${di}`);
    }
    return next;
  });

  const handlePaymentSuccess = async (paymentIntentId) => {
    const sessions = [];
    workshop.days.forEach((day, di) => {
      day.sessions.forEach(sess => {
        if (selected.has("full") || selected.has(`day__${di}`) || selected.has(`${di}__${sess.id}`)) {
          sessions.push({ id: `${di}__${sess.id}`, date: day.date, label: sess.label, time: sess.time });
        }
      });
    });
    try {
      const bookRes  = await fetch(`${API}/bookings/workshop`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          name, email,
          workshopId: workshop.id,
          workshopTitle: workshop.title,
          sessions, totalPrice,
          paymentType: "dropin",
          stripePaymentId: paymentIntentId,
        }),
      });
      const bookData = await bookRes.json();
      if (!bookRes.ok) throw new Error(bookData.error ?? "Booking failed.");
      setBooked(true);
    } catch (err) {
      setBookError(err.message);
    }
  };

  if (booked) {
    return (
      <div className="modal-overlay" onClick={onClose}>
        <div className="modal-card workshop-booking-modal" onClick={e => e.stopPropagation()}>
          <button className="modal-close" onClick={onClose}>✕</button>
          <div className="booking-success">
            <div className="booking-success-icon">✓</div>
            <h2 className="booking-success-title">You're booked!</h2>
            <p className="booking-success-sub">Check your email for confirmation details.</p>
            <button className="book-btn" style={{marginTop:"1.25rem"}} onClick={onClose}>Done</button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-card workshop-booking-modal" onClick={e => e.stopPropagation()}>
        <button className="modal-close" onClick={onClose}>✕</button>
        <div className="modal-badge badge-workshop">Workshop</div>
        <h2 className="modal-title">{workshop.title}</h2>
        {workshop.description && <p className="workshop-desc">{workshop.description}</p>}
        <p className="workshop-teacher">with {workshop.teacher}</p>

        <div className={`booking-option full-workshop-option ${selected.has("full") ? "selected" : ""}`} onClick={toggleFull}>
          <div className="booking-option-left">
            <span className="booking-option-label">Full Workshop</span>
            <span className="booking-option-sub">All days · all sessions</span>
          </div>
          <span className="booking-option-price">${workshop.pricing.fullWorkshop}</span>
          <span className="booking-option-check">{selected.has("full") ? "✓" : ""}</span>
        </div>

        <div className="booking-divider">— or choose individual days / sessions —</div>

        {workshop.days.map((day, di) => {
          const dayDate     = parseLocalDate(day.date);
          const dayLabel    = dayDate.toLocaleDateString("en-US", {weekday:"short", month:"short", day:"numeric"});
          const venue       = venues.find(v => v.id === day.venue);
          const daySelected = selected.has(`day__${di}`);
          return (
            <div key={di} className="booking-day-block">
              <div className="booking-day-header">
                <div>
                  <span className="booking-day-label">{dayLabel}</span>
                  <span className="booking-day-venue">{venue?.name ?? day.venue}</span>
                </div>
                <button className={`booking-day-btn ${daySelected ? "selected" : ""}`} onClick={() => toggleDay(di)}>
                  Full Day — ${workshop.pricing.fullDay}{daySelected ? " ✓" : ""}
                </button>
              </div>
              {!daySelected && !selected.has("full") && day.sessions.map(sess => {
                const key   = `${di}__${sess.id}`;
                const isSel = selected.has(key);
                return (
                  <div key={sess.id} className={`booking-option session-option ${isSel ? "selected" : ""}`} onClick={() => toggle(key)}>
                    <div className="booking-option-left">
                      <span className="booking-option-label">{sess.label}</span>
                      <span className="booking-option-sub">{formatTime(sess.time)} · {sess.duration} min</span>
                    </div>
                    <span className="booking-option-price">${sess.price}</span>
                    <span className="booking-option-check">{isSel ? "✓" : ""}</span>
                  </div>
                );
              })}
            </div>
          );
        })}

        <div className="booking-fields" style={{marginTop:"1.25rem"}}>
          <label className="form-label">Full Name
            <input className="form-input" value={name} onChange={e => setName(e.target.value)} placeholder="e.g. Jane Smith" />
          </label>
          <label className="form-label">Email
            <input className="form-input" type="email" value={email} onChange={e => setEmail(e.target.value)} placeholder="your@email.com" />
          </label>
        </div>

        <div className="booking-total" style={{margin:"0.75rem 0"}}>Total: <strong>${totalPrice}</strong></div>

        <StripeCardForm
          amount={Math.round(totalPrice * 100)}
          email={email}
          billingDetails={{ email }}
          onSuccess={handlePaymentSuccess}
          onError={(msg) => setBookError(msg)}
          submitLabel="Book Selected Sessions"
          disabled={selected.size === 0 || !name.trim() || !email.trim()}
        />
        {bookError && <p className="pass-feedback invalid" style={{marginTop:"0.75rem"}}>{bookError}</p>}
      </div>
    </div>
  );
}
